import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";

import NavbarPage from "../../components/Navbar/Navbar_Page";
import Section from "./section";
import Contact from "../../components/Contact/Contact";
import Social from "../../components/Social/Social";
import Footer from "../../components/Footer/footer";

import "./Index10.css";

const INTEGRATIONS = [
  { name: "Slack", color: "#4A154B" },
  { name: "Jira", color: "#0052CC" },
  { name: "GitHub", color: "#6e7681" },
  { name: "GitLab", color: "#FC6D26" },
  { name: "Linear", color: "#5E6AD2" },
  { name: "Asana", color: "#F06A6A" },
  { name: "Intercom", color: "#1F8DED" },
  { name: "Zendesk", color: "#03363D" },
  { name: "Salesforce", color: "#00A1E0" },
  { name: "HubSpot", color: "#FF7A59" },
  { name: "Notion", color: "#999" },
  { name: "Teams", color: "#6264A7" },
  { name: "Azure DevOps", color: "#0078D7" },
  { name: "ClickUp", color: "#7B68EE" },
  { name: "Help Scout", color: "#1292EE" },
  { name: "Freshdesk", color: "#25C16F" },
  { name: "Zapier", color: "#FF4A00" },
  { name: "Segment", color: "#52BD94" },
  { name: "Webhooks", color: "#4f46e5" },
  { name: "Discord", color: "#5865F2" },
];

const BENTO_FEATURES = [
  {
    icon: "pe-7s-attention",
    title: "Issue Tracking",
    desc: "Customers report bugs with file attachments, priority levels, and status tracking. Never lose a report again.",
    accent: "purple",
  },
  {
    icon: "pe-7s-map-2",
    title: "Public Roadmap",
    desc: "Share a beautiful kanban board your customers can vote on, filter, and follow. Full transparency builds trust.",
    accent: "teal",
    wide: true,
    featured: true,
    visual: true,
  },
  {
    icon: "pe-7s-plugin",
    title: "Feature Requests",
    desc: "Let users tell you what they need. Upvoting surfaces the highest-impact ideas to the top.",
    accent: "pink",
  },
  {
    icon: "pe-7s-shield",
    title: "AI Spam Detection",
    desc: "Behavioral and text analysis filters out noise so your board stays clean and actionable.",
    accent: "blue",
  },
  {
    icon: "pe-7s-news-paper",
    title: "Changelog",
    desc: "Announce shipped features and link them back to the feedback that inspired them. Close the loop.",
    accent: "orange",
  },
  {
    icon: "pe-7s-copy-file",
    title: "Duplicate Detection",
    desc: "Smart suggestions surface similar posts during submission. Consolidate feedback automatically.",
    accent: "green",
  },
  {
    icon: "pe-7s-tools",
    title: "SDK, API & MCP",
    desc: "Full-featured SDKs in 7+ languages plus an MCP server, so your tools — and your AI assistants — can work the backlog.",
    accent: "purple",
    wide: true,
  },
  {
    icon: "pe-7s-cloud-upload",
    title: "20 Integrations",
    desc: "Sync with Jira, Slack, GitHub, Linear, Salesforce, HubSpot, and 14 more tools you already use.",
    accent: "teal",
  },
];

const WIDGET_FEATURES = [
  {
    icon: "pe-7s-browser",
    title: "Inline or modal",
    desc: "Render it in your page flow, or as a modal behind a floating launcher button — one attribute switches the layout.",
  },
  {
    icon: "pe-7s-lock",
    title: "Private backlogs included",
    desc: "Identify your logged-in customers with a signed token and members see private backlogs right in the widget.",
  },
  {
    icon: "pe-7s-paint-bucket",
    title: "White-label ready",
    desc: "Higher tiers remove all PublicBacklog branding automatically — no configuration in the embed.",
  },
  {
    icon: "pe-7s-shield",
    title: "Zero style conflicts",
    desc: "Shadow-DOM isolated with light and dark themes. Your CSS never touches the widget, and vice versa.",
  },
];

const PROCESS_STEPS = [
  {
    num: "01",
    title: "Create a backlog",
    desc: "Set up your feedback board in under 2 minutes. Public or private.",
  },
  {
    num: "02",
    title: "Collect feedback",
    desc: "Customers submit bugs, requests, and ideas. Voting surfaces priorities.",
  },
  {
    num: "03",
    title: "Triage & sync",
    desc: "Manage tickets on your kanban. Auto-sync to Jira, Linear, GitHub, and more.",
  },
  {
    num: "04",
    title: "Ship & announce",
    desc: "Mark done, publish to changelog, and close the loop with voters.",
  },
];

const Index10 = () => {
  const [navItems] = useState([
    { id: 1, idnm: "home", navheading: "Home" },
    { id: 2, idnm: "services", navheading: "Features" },
    { id: 3, link: "/features", navheading: "Product Tour" },
    { id: 4, idnm: "contact", navheading: "Contact" },
  ]);

  useEffect(() => {
    fetch("https://api.cmsoftware.biz/landing/log-visit", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({}),
    }).catch(() => {});
  }, []);

  return (
    <div className="home-ten">
      {/* Animated background blobs */}
      <div className="ht-mesh-bg">
        <div className="ht-blob ht-blob-1" />
        <div className="ht-blob ht-blob-2" />
        <div className="ht-blob ht-blob-3" />
        <div className="ht-blob ht-blob-4" />
      </div>

      {/* Noise texture overlay */}
      <div className="ht-noise" />

      {/* Content */}
      <div className="ht-content">
        <NavbarPage navItems={navItems} navClass="" />

        {/* Hero */}
        <Section />

        {/* Bento Features */}
        <section className="ht-features" id="services">
          <div className="ht-features-inner">
            <div className="ht-section-label">
              <span
                style={{
                  width: 16,
                  height: 2,
                  background: "currentColor",
                  display: "inline-block",
                }}
              />
              Features
            </div>
            <h2 className="ht-section-title">
              Everything you need to{" "}
              <span className="ht-gradient-text">close the feedback loop</span>
            </h2>
            <p className="ht-section-desc">
              From bug reports to shipped features, Public Backlog is the complete
              feedback platform. Collect, prioritize, build, and announce — all in
              one place.
            </p>

            <div className="ht-bento">
              {BENTO_FEATURES.map((f, i) => (
                <div
                  key={i}
                  className={[
                    "ht-bento-card",
                    `ht-bento-accent-${f.accent}`,
                    f.wide ? "ht-bento-wide" : "",
                    f.tall ? "ht-bento-tall" : "",
                    f.featured ? "ht-bento-featured" : "",
                  ]
                    .filter(Boolean)
                    .join(" ")}
                >
                  <div className="ht-bento-icon">
                    <i className={f.icon} />
                  </div>
                  <div className="ht-bento-title">{f.title}</div>
                  <div className="ht-bento-desc">{f.desc}</div>

                  {f.visual && (
                    <div className="ht-bento-visual">
                      <div className="ht-bento-bar-chart">
                        {[40, 65, 45, 80, 55, 90, 70, 85, 60, 95].map(
                          (h, j) => (
                            <div
                              key={j}
                              className="ht-bar"
                              style={{
                                height: `${h}%`,
                                background: `linear-gradient(to top, rgba(79,70,229,0.6), rgba(129,140,248,0.6))`,
                                animationDelay: `${j * 0.1}s`,
                              }}
                            />
                          )
                        )}
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>

            <div style={{ textAlign: "center", marginTop: 48 }}>
              <Link to="/features" className="ht-btn-secondary">
                Explore all features
                <span style={{ fontSize: "1.1em" }}>&rarr;</span>
              </Link>
            </div>
          </div>
        </section>

        {/* Embeddable Widget Showcase */}
        <section className="ht-widget" id="widget">
          <div className="ht-widget-inner">
            <div className="ht-widget-copy">
              <div className="ht-section-label">
                <span
                  style={{
                    width: 16,
                    height: 2,
                    background: "currentColor",
                    display: "inline-block",
                  }}
                />
                Embeddable Widget
              </div>
              <h2 className="ht-section-title">
                Your backlog,{" "}
                <span className="ht-gradient-text">on your website</span>
              </h2>
              <p className="ht-section-desc" style={{ marginBottom: 32 }}>
                One script tag embeds your public backlog, your roadmap, a
                feedback form, a docs-feedback prompt, or your changelog
                directly into your own site. Visitors browse, vote, and submit
                without ever leaving your page.
              </p>

              <div className="ht-widget-features">
                {WIDGET_FEATURES.map((f, i) => (
                  <div key={i} className="ht-widget-feature">
                    <div className="ht-widget-feature-icon">
                      <i className={f.icon} />
                    </div>
                    <div>
                      <div className="ht-widget-feature-title">{f.title}</div>
                      <div className="ht-widget-feature-desc">{f.desc}</div>
                    </div>
                  </div>
                ))}
              </div>

              <a
                href="https://docs.publicbacklog.com/#/widget/"
                target="_blank"
                rel="noopener noreferrer"
                className="ht-btn-secondary"
                style={{ marginTop: 36 }}
              >
                Widget docs
                <span style={{ fontSize: "1.1em" }}>&rarr;</span>
              </a>
            </div>

            <div className="ht-widget-visual">
              <div className="ht-code-card">
                <div className="ht-dashboard-header">
                  <span
                    className="ht-dashboard-dot"
                    style={{ background: "#ff5f57" }}
                  />
                  <span
                    className="ht-dashboard-dot"
                    style={{ background: "#febc2e" }}
                  />
                  <span
                    className="ht-dashboard-dot"
                    style={{ background: "#28c840" }}
                  />
                  <span className="ht-code-filename">index.html</span>
                </div>
                <pre className="ht-code-body">
                  <code>
                    <span className="ht-code-tag">&lt;script</span>{" "}
                    <span className="ht-code-attr">src</span>=
                    <span className="ht-code-str">
                      "https://app.publicbacklog.com/widget/publicbacklog-widget.min.js"
                    </span>{" "}
                    <span className="ht-code-attr">defer</span>
                    <span className="ht-code-tag">&gt;&lt;/script&gt;</span>
                    {"\n\n"}
                    <span className="ht-code-tag">&lt;div</span>
                    {"\n  "}
                    <span className="ht-code-attr">data-pb-widget</span>=
                    <span className="ht-code-str">"backlog"</span>
                    {"\n  "}
                    <span className="ht-code-attr">data-pb-org</span>=
                    <span className="ht-code-str">"YourOrganization"</span>
                    {"\n  "}
                    <span className="ht-code-attr">data-pb-backlog</span>=
                    <span className="ht-code-str">"Feedback"</span>
                    {"\n  "}
                    <span className="ht-code-attr">data-pb-mode</span>=
                    <span className="ht-code-str">"modal"</span>
                    {"\n  "}
                    <span className="ht-code-attr">data-pb-launcher</span>=
                    <span className="ht-code-str">"Feedback"</span>
                    <span className="ht-code-tag">&gt;</span>
                    {"\n"}
                    <span className="ht-code-tag">&lt;/div&gt;</span>
                  </code>
                </pre>
              </div>

              <div className="ht-widget-launcher">
                <span className="ht-widget-launcher-icon">
                  <i className="pe-7s-comment" />
                </span>
                Feedback
              </div>
            </div>
          </div>
        </section>

        {/* Integrations Marquee */}
        <section className="ht-integrations">
          <div className="ht-integrations-inner">
            <div className="ht-section-label">
              <span
                style={{
                  width: 16,
                  height: 2,
                  background: "currentColor",
                  display: "inline-block",
                }}
              />
              Integrations
            </div>
            <h2 className="ht-section-title">
              Plays nice with your <span className="ht-gradient-text">entire stack</span>
            </h2>
            <p className="ht-section-desc">
              Two-way sync with the tools you already use. No copy-pasting, no
              context switching.
            </p>
          </div>

          <div className="ht-marquee-wrap">
            <div className="ht-marquee">
              {/* Duplicate the list for seamless loop */}
              {[...INTEGRATIONS, ...INTEGRATIONS].map((intg, i) => (
                <span key={i} className="ht-integration-pill">
                  <span
                    className="ht-integration-dot"
                    style={{ background: intg.color }}
                  />
                  {intg.name}
                </span>
              ))}
            </div>
          </div>
        </section>

        {/* How It Works */}
        <section className="ht-process" id="process">
          <div className="ht-process-inner">
            <div className="ht-section-label">
              <span
                style={{
                  width: 16,
                  height: 2,
                  background: "currentColor",
                  display: "inline-block",
                }}
              />
              How it works
            </div>
            <h2 className="ht-section-title">
              Four steps to{" "}
              <span className="ht-gradient-text">customer-driven development</span>
            </h2>

            <div className="ht-process-grid">
              {PROCESS_STEPS.map((step, i) => (
                <div key={i} className="ht-process-step">
                  <div className="ht-process-number">{step.num}</div>
                  <div className="ht-process-step-title">{step.title}</div>
                  <div className="ht-process-step-desc">{step.desc}</div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Big CTA */}
        <section className="ht-cta">
          <div className="ht-cta-glow" />
          <div className="ht-cta-inner">
            <h2 className="ht-cta-title">
              Ready to hear what your customers{" "}
              <span className="ht-gradient-text">really think?</span>
            </h2>
            <p className="ht-cta-desc">
              Join hundreds of teams using Public Backlog to turn customer feedback
              into their competitive advantage. Free forever on the starter plan.
            </p>
            <div className="ht-cta-actions">
              <a
                href="https://app.publicbacklog.com/user/login"
                className="ht-btn-primary"
              >
                Get started for free
                <span style={{ fontSize: "1.1em" }}>&rarr;</span>
              </a>
              <a
                href="https://docs.publicbacklog.com"
                target="_blank"
                rel="noopener noreferrer"
                className="ht-btn-secondary"
              >
                Read the docs
              </a>
            </div>
          </div>
        </section>

        {/* Contact */}
        <Contact />

        {/* Social */}
        <Social />

        {/* Footer */}
        <Footer />
      </div>
    </div>
  );
};

export default Index10;
